import { IconButton, Typography } from '@amway/react-components';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import TealiumDataLayer from '../../components/hocs/tealium-data-layer';
import MultiSelectDropdown from '../../components/ui/multi-select-dropdown';
import SearchInputComponent from '../../components/ui/search-input';
import useAuth from '../../resources/auth/auth-hook';
import useTranslationList from '../../resources/translations/translations-hook';
import { Translation } from '../../resources/translations/translations-model';
import './index.scss';
import TranslationToolAddComponent from './translation-tool-add';
import TranslationsToolEdit from './translations-edit';
import TranslationsToolPaginatedList from './translations-paginated-list';
import { ModalComponent } from '../../components/ui/modal';

export default function TranslationsScreenComponent() {
  const {
    listTranslations,
    fetchListTranslations,
    locals,
    updateTranslationStatus,
    addTranslationStatus,
    removeTranslation,
    removeTranslationStatus,
    listTranslationProperties,
    fetchListTranslationProperties,
  } = useTranslationList();
  const [rule, setRule] = useState<Translation | undefined | null>();
  const [localeFilter, setLocaleFilter] = useState<string[] | undefined>();
  const [translateFrom, setTranslateFrom] = useState('');
  const [showModal, setShowModal] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (updateTranslationStatus.success || addTranslationStatus.success || removeTranslationStatus.success) {
      fetchListTranslations();
    }
  }, [fetchListTranslations, updateTranslationStatus, addTranslationStatus, removeTranslationStatus]);

  useEffect(() => {
    fetchListTranslations();
  }, [fetchListTranslations]);
  useEffect(() => {
    fetchListTranslationProperties();
  }, [fetchListTranslationProperties]);
  const handleSubmit = (event: any) => {
    event.preventDefault();
    const localeParam = localeFilter?.includes('all') ? undefined : localeFilter;
    const translateFromParam = translateFrom.trim() || undefined;
    fetchListTranslations(localeParam, translateFromParam);
  };

  const handleAddClick = useCallback(() => {
    setRule(null);
  }, []);

  const handleEditClick = useCallback((rule: Translation) => {
    setRule(rule);
  }, []);

  const handleDeleteClick = useCallback(
    (rule: Translation) => {
      removeTranslation(rule.id!);
    },
    [removeTranslation],
  );

  const handleBackClick = useCallback(() => {
    setRule(undefined);
  }, []);

  const handleLocaleChanges = useCallback((newLocales?: string[]) => {
    if (newLocales && newLocales.length > 0) setLocaleFilter(newLocales);
    else setLocaleFilter(undefined);
  }, []);

  const localsFiltered = locals.map(e => ({
    name: e,
    value: e,
  }));

  return (
    <TealiumDataLayer
      page_name="Translations tool"
      page_section="translations-tool"
      page_category="Configuration"
      page_subCategory="Set up translations">
      <Container className="translations-tool">
        {rule === undefined ? (
          <Card>
            <Card.Body>
              <Row>
                <Col md={10}>
                  <Typography variant="heading">Translations</Typography>
                  <Typography weight="bold" color="text-gray" className="mt-3">
                    View and update translations below.
                  </Typography>
                </Col>
                <Col md={2} className="d-flex justify-content-end">
                  <IconButton styleType="link" onClick={handleAddClick}>
                    New translation
                  </IconButton>
                </Col>
                <Col></Col>
              </Row>
              <Form className="translation-form" onSubmit={handleSubmit}>
                <Form.Group>
                  <Row>
                    <Col md={4}>
                      <MultiSelectDropdown
                        id="locales-dropdown"
                        label="Locales"
                        placeholder="Locales"
                        variant="secondary"
                        all
                        items={localsFiltered}
                        value={localeFilter}
                        onChange={handleLocaleChanges}></MultiSelectDropdown>
                    </Col>
                    <Col md={4}>
                      <SearchInputComponent
                        id="search-translation-input"
                        label="Search"
                        placeholder="Translate From"
                        value={translateFrom}
                        onChange={setTranslateFrom}
                      />
                    </Col>
                    <Col
                      xs={12}
                      md={4}
                      lg={4}
                      xl={2}
                      xxl={2}
                      className="submit d-flex mt-1 mb-4 justify-content-center align-content-center align-items-end">
                      <Button type="submit" className="search-btn">
                        SEARCH
                      </Button>
                    </Col>
                  </Row>
                </Form.Group>
              </Form>
              <Alert variant={'primary'}>
                Translations only work for pre-defined properties.<br></br>
                <button className="translation-properties-btn" onClick={() => setShowModal(true)}>
                  Click here to see the properties that translations are enabled
                </button>
              </Alert>
              <ModalComponent
                show={showModal}
                onHide={() => setShowModal(false)}
                title={'Translation properties enabled'}
                body={listTranslationProperties}
                confirmationModal={false}
              />
              <TranslationsToolPaginatedList
                translationsToolData={listTranslations}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
              />
            </Card.Body>
          </Card>
        ) : rule === null ? (
          <TranslationToolAddComponent onBackClick={handleBackClick} user={user?.id} />
        ) : (
          <TranslationsToolEdit rule={rule} onBackClick={handleBackClick} user={user?.id} />
        )}
      </Container>
    </TealiumDataLayer>
  );
}
